import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { App as CapApp } from "@capacitor/app";
import SportsBioIcon from "./SportsBioIcon";
import { Button } from "./ui/button";
import { registerExitRequestListener } from "@/hooks/useBackButton";

/**
 * In-app exit confirmation dialog matching SportsBio's dark theme.
 * Mounted once at the root (App.tsx). Triggered via the hook's
 * `registerExitRequestListener` so the back-button flow can ask for confirm
 * without using Capacitor's native alert.
 */
const ExitConfirmDialog = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    return registerExitRequestListener(() => setOpen(true));
  }, []);

  const handleStay = () => setOpen(false);

  const handleExit = () => {
    setOpen(false);
    // Native-only — on web this is a no-op.
    void CapApp.exitApp().catch(() => { /* ignore */ });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <div
            role="button"
            aria-label="Stay in app"
            tabIndex={-1}
            className="absolute inset-0 bg-background/90 backdrop-blur-md"
            onClick={handleStay}
          />
          <motion.div
            className="relative w-full max-w-sm bg-card border border-border rounded-2xl p-6 text-center shadow-2xl"
            initial={{ scale: 0.94, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 8 }}
            transition={{ type: "spring", damping: 22, stiffness: 320 }}
          >
            <div className="flex justify-center mb-4">
              <SportsBioIcon size={48} />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground">Exit SportsBio?</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">Are you sure you want to leave?</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleStay} className="flex-1">
                Stay
              </Button>
              <Button variant="destructive" onClick={handleExit} className="flex-1">
                Exit
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitConfirmDialog;
